import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GameOutput } from '../generated-api';
import { useRecentGames } from '../hooks/useRecentGames';
import RecentGamesQuickSelect from './RecentGamesQuickSelect';
import SelectedGameCard from './SelectedGameCard';
import PlayMetadataSection from './PlayMetadataSection';
import PhotoStripSection from './PhotoStripSection';
import GameSelectionWidget from './GameSelectionWidget';

interface GameDetailsFormProps {
  games: GameOutput[];
  selectedGameId: string;
  selectedGame: GameOutput | null;
  gameExtensions: string[];
  playDate: string;
  comment: string;
  durationMinutes?: number | null;
  playImages?: File[];
  existingImages?: string[];
  teams?: string[];
  teamMetadata?: Record<string, Record<string, any>>;
  onTeamMetadataChange?: (metadata: Record<string, Record<string, any>>) => void;
  onGameSelect: (gameId: string) => void;
  onToggleExtension: (extension: string) => void;
  onPlayDateChange: (date: string) => void;
  onCommentChange: (comment: string) => void;
  onDurationChange?: (minutes: number | null) => void;
  onPlayImagesChange?: (images: File[]) => void;
  onDeleteExistingImage?: (imageUrl: string) => void;
  onEditGame?: () => void;
  showAddGameLink?: boolean;
  readOnly?: boolean;
  gameSelectionReadOnly?: boolean;
}

const GameDetailsForm: React.FC<GameDetailsFormProps> = ({
  games,
  selectedGameId,
  selectedGame,
  gameExtensions,
  playDate,
  comment,
  durationMinutes,
  playImages = [],
  existingImages = [],
  teams = [],
  teamMetadata,
  onTeamMetadataChange,
  onGameSelect,
  onToggleExtension,
  onPlayDateChange,
  onCommentChange,
  onDurationChange,
  onPlayImagesChange,
  onDeleteExistingImage,
  onEditGame,
  showAddGameLink = true,
  readOnly = false,
  gameSelectionReadOnly = false,
}) => {
  const [showFullSelector, setShowFullSelector] = useState(false);
  const { recentGames, loading: recentGamesLoading } = useRecentGames(4);

  const handleGameSelect = (gameId: string) => {
    onGameSelect(gameId);
    setShowFullSelector(false);
  };

  const handleShowAllGames = () => {
    setShowFullSelector(true);
  };

  const handleChangeGame = () => {
    onGameSelect('');
    setShowFullSelector(false);
  };

  const handleCloseFullSelector = () => {
    setShowFullSelector(false);
  };

  // Read-only mode - simplified display
  if (readOnly) {
    return (
      <div className="bg-surface shadow-md rounded-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-50 to-indigo-50 px-6 py-4 border-b border-primary-100">
          <h2 className="text-lg font-semibold text-heading">Play Details</h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Selected Game */}
          {selectedGame && (
            <SelectedGameCard
              game={selectedGame}
              gameExtensions={gameExtensions}
              onChangeGame={() => {}}
              onToggleExtension={onToggleExtension}
              readOnly={true}
            />
          )}

          {/* Metadata */}
          <PlayMetadataSection
            playDate={playDate}
            comment={comment}
            durationMinutes={durationMinutes}
            onPlayDateChange={onPlayDateChange}
            onCommentChange={onCommentChange}
            onDurationChange={onDurationChange}
            teams={teams}
            teamMetadata={teamMetadata}
            onTeamMetadataChange={onTeamMetadataChange}
            readOnly={true}
            playerMetadataFields={selectedGame?.playerMetadataFields || []}
          />

          {/* Photos */}
          {existingImages.length > 0 && (
            <PhotoStripSection
              playImages={[]}
              existingImages={existingImages}
              onPlayImagesChange={() => {}}
              readOnly={true}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface shadow-md rounded-xl overflow-hidden" data-testid="game-details-form">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-50 to-indigo-50 px-6 py-4 border-b border-primary-100">
        <h2 className="text-lg font-semibold text-heading">Play Details</h2>
      </div>

      <div className="p-6">
        {/* Game Selection Section */}
        <div className="mb-6">
          {!gameSelectionReadOnly && (
            <label className="block text-sm font-medium text-body mb-3">
              Select Game <span className="text-red-500">*</span>
            </label>
          )}

          {/* Show selected game card OR selection options */}
          {selectedGame && (!showFullSelector || gameSelectionReadOnly) ? (
            <SelectedGameCard
              game={selectedGame}
              gameExtensions={gameExtensions}
              onChangeGame={gameSelectionReadOnly ? () => {} : handleChangeGame}
              onToggleExtension={onToggleExtension}
              onEditClick={onEditGame}
              readOnly={gameSelectionReadOnly}
            />
          ) : showFullSelector && !gameSelectionReadOnly ? (
            /* Full game selector */
            <div>
              <GameSelectionWidget
                games={games}
                selectedGameId={selectedGameId}
                selectedGame={selectedGame}
                onGameSelect={handleGameSelect}
                autoExpand={true}
              />
              {recentGames.length > 0 && (
                <button
                  type="button"
                  onClick={handleCloseFullSelector}
                  className="mt-3 text-sm text-muted hover:text-body"
                >
                  ← Back to recent games
                </button>
              )}
            </div>
          ) : !gameSelectionReadOnly ? (
            /* Recent games quick select */
            <div>
              <RecentGamesQuickSelect
                recentGames={recentGames}
                selectedGameId={selectedGameId}
                onGameSelect={handleGameSelect}
                onShowAllGames={handleShowAllGames}
                loading={recentGamesLoading}
              />
              {!selectedGameId && showAddGameLink && (
                <p className="text-xs text-muted mt-3">
                  Don't see your game?{' '}
                  <Link to="/games/new" className="text-primary-600 hover:text-primary-800 font-medium">
                    Add it to your library
                  </Link>
                </p>
              )}
            </div>
          ) : null}
        </div>

        {/* Progressive disclosure - show metadata and photos after game is selected */}
        {selectedGame && (
          <div className="space-y-6 animate-fadeIn">
            {/* Divider */}
            <div className="border-t border-edge-subtle" />

            {/* Metadata Section - Date, Comments, and Duration */}
            <PlayMetadataSection
              playDate={playDate}
              comment={comment}
              durationMinutes={durationMinutes}
              onPlayDateChange={onPlayDateChange}
              onCommentChange={onCommentChange}
              onDurationChange={onDurationChange}
              teams={teams}
              teamMetadata={teamMetadata}
              onTeamMetadataChange={onTeamMetadataChange}
              hideDateButton={gameSelectionReadOnly}
              playerMetadataFields={selectedGame?.playerMetadataFields || []}
            />

            {/* Photos Section */}
            {onPlayImagesChange && (
              <>
                <div className="border-t border-edge-subtle" />
                <PhotoStripSection
                  playImages={playImages}
                  existingImages={existingImages}
                  onPlayImagesChange={onPlayImagesChange}
                  onDeleteExistingImage={onDeleteExistingImage}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Add animation styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default GameDetailsForm;
